import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Award, CheckCircle2, ClipboardList, Clock, XCircle } from 'lucide-react';
import { Button, Card, CardContent, Textarea, toast } from '@mlm/ui';
import {
  getCourseTest,
  getTestAttempt,
  submitTestAttempt,
  type CourseTestState,
  type MemberAttempt,
} from '../lib/api';

/**
 * The test pane — swaps in for the video pane when a test row is selected
 * (`?test=`). Intro (question count, passing mark, attempts used) → taking
 * (radio choices + typed answers) → result (passed / failed with Retake /
 * awaiting review). Graded results show per-question correctness and the
 * instructor's feedback, never the correct answer itself.
 */
export function CourseTestPane({
  courseId,
  test,
  locked,
}: {
  courseId: string;
  test: CourseTestState;
  locked: boolean;
}) {
  const qc = useQueryClient();
  const [taking, setTaking] = useState(false);
  const [answers, setAnswers] = useState<Record<string, { selectedIndex?: number; text?: string }>>(
    {},
  );
  const [justSubmitted, setJustSubmitted] = useState<MemberAttempt | null>(null);

  const testQ = useQuery({
    queryKey: ['course-test', courseId, test.id],
    queryFn: () => getCourseTest(courseId, test.id),
    enabled: taking && !locked,
  });
  const latest = justSubmitted ?? null;
  const [viewAttemptId, setViewAttemptId] = useState<string | null>(null);
  const attemptQ = useQuery({
    queryKey: ['course-test-attempt', courseId, test.id, viewAttemptId],
    queryFn: () => getTestAttempt(courseId, test.id, viewAttemptId!),
    enabled: !!viewAttemptId,
  });

  const submit = useMutation({
    mutationFn: () =>
      submitTestAttempt(
        courseId,
        test.id,
        (testQ.data?.questions ?? []).map((q) => ({
          questionId: q.id,
          ...(q.kind === 'multiple_choice'
            ? { selectedIndex: answers[q.id]?.selectedIndex ?? null }
            : { text: answers[q.id]?.text ?? null }),
        })),
      ),
    onSuccess: (res) => {
      setTaking(false);
      setAnswers({});
      setJustSubmitted(res.attempt);
      qc.invalidateQueries({ queryKey: ['course-player', courseId] });
      qc.invalidateQueries({ queryKey: ['my-courses'] });
      if (test.isPractice) {
        toast.success('Practice submitted — the answers are below.');
      } else if (res.attempt.status === 'passed') {
        toast.success(
          res.certificateIssued
            ? 'Passed — your certificate is ready! 🎉'
            : 'Test passed — well done!',
        );
      } else if (res.attempt.status === 'pending_review') {
        toast.success('Submitted — your instructor will review your answers.');
      }
    },
    onError: (e: unknown) => {
      const code = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(
        code === 'attempt_pending'
          ? 'Your previous attempt is still being reviewed.'
          : code === 'already_passed'
            ? 'You already passed this test.'
            : code === 'attempt_limit_reached'
              ? 'No attempts left for this test.'
              : 'Could not submit — try again.',
      );
    },
  });

  if (locked) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Unlock this course to take “{test.title}”.
        </CardContent>
      </Card>
    );
  }

  // ── taking ──
  if (taking) {
    const questions = testQ.data?.questions ?? [];
    const answered = questions.filter((q) =>
      q.kind === 'multiple_choice'
        ? answers[q.id]?.selectedIndex !== undefined
        : (answers[q.id]?.text ?? '').trim().length > 0,
    ).length;
    return (
      <Card>
        <CardContent className="space-y-5 py-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">{test.title}</h2>
            <span className="text-xs text-muted-foreground">
              {answered}/{questions.length} answered
            </span>
          </div>
          {questions.map((q, i) => (
            <div key={q.id} className="space-y-2">
              <p className="text-sm font-medium">
                {i + 1}. {q.prompt}{' '}
                <span className="font-normal text-muted-foreground">
                  ({q.points} pt{q.points === 1 ? '' : 's'})
                </span>
              </p>
              {q.kind === 'multiple_choice' ? (
                <div className="space-y-1">
                  {q.choices.map((c, ci) => (
                    <label
                      key={ci}
                      className={`flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-sm ${
                        answers[q.id]?.selectedIndex === ci
                          ? 'border-primary bg-accent'
                          : 'hover:bg-muted/40'
                      }`}
                    >
                      <input
                        type="radio"
                        name={q.id}
                        checked={answers[q.id]?.selectedIndex === ci}
                        onChange={() =>
                          setAnswers((a) => ({ ...a, [q.id]: { selectedIndex: ci } }))
                        }
                      />
                      {c}
                    </label>
                  ))}
                </div>
              ) : (
                <Textarea
                  rows={4}
                  maxLength={8000}
                  placeholder="Type your answer…"
                  value={answers[q.id]?.text ?? ''}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: { text: e.target.value } }))}
                />
              )}
            </div>
          ))}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setTaking(false)}>
              Cancel
            </Button>
            <Button
              disabled={questions.length === 0 || answered < questions.length || submit.isPending}
              onClick={() => submit.mutate()}
            >
              Submit answers
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── result of the attempt just submitted (or a reviewed past attempt) ──
  const shownAttempt = viewAttemptId ? attemptQ.data : latest;
  if (shownAttempt) {
    return (
      <AttemptResult
        attempt={shownAttempt}
        test={test}
        onRetake={test.canAttempt ? () => setTaking(true) : null}
        onBack={() => {
          setJustSubmitted(null);
          setViewAttemptId(null);
        }}
      />
    );
  }

  // ── intro ──
  return (
    <Card>
      <CardContent className="space-y-4 py-6">
        <div className="space-y-1 text-center">
          {test.isPractice ? (
            <ClipboardList className="mx-auto h-10 w-10 text-muted-foreground" />
          ) : test.passed ? (
            <Award className="mx-auto h-10 w-10 text-primary" />
          ) : (
            <Clock className="mx-auto h-10 w-10 text-muted-foreground" />
          )}
          <h2 className="text-lg font-semibold">{test.title}</h2>
          {test.description && (
            <p className="mx-auto max-w-md text-sm text-muted-foreground">{test.description}</p>
          )}
          <p className="text-sm text-muted-foreground">
            {test.questionCount} question{test.questionCount === 1 ? '' : 's'} · pass mark{' '}
            {test.passingScorePct}%
            {!test.isPractice &&
              test.maxAttempts != null &&
              ` · ${Math.max(0, test.maxAttempts - test.attempts.length)} of ${test.maxAttempts} attempts left`}
          </p>
          {test.isPractice && (
            <p className="mx-auto max-w-md text-xs text-muted-foreground">
              Practice test — it never counts toward your certificate. Retake it as often as you
              like; the answers and explanations appear as soon as you submit.
            </p>
          )}
        </div>
        <div className="flex justify-center">
          {test.isPractice ? (
            // Practice has no terminal state: a pass is not a reason to stop
            // offering it, so the button is driven by `canAttempt` alone.
            <Button onClick={() => setTaking(true)} disabled={!test.canAttempt}>
              {test.attempts.length > 0 ? 'Retake practice' : 'Start practice'}
            </Button>
          ) : test.passed ? (
            <p className="text-sm font-medium text-primary">You passed this test ✓</p>
          ) : test.attempts.some((a) => a.status === 'pending_review') ? (
            <p className="text-sm text-muted-foreground">
              Submitted — awaiting your instructor's review.
            </p>
          ) : test.canAttempt ? (
            <Button onClick={() => setTaking(true)}>
              {test.attempts.length > 0 ? 'Retake test' : 'Start test'}
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">No attempts left.</p>
          )}
        </div>
        {test.attempts.length > 0 && (
          <div className="mx-auto max-w-md">
            <p className="mb-1 text-xs font-semibold text-muted-foreground">Your attempts</p>
            <ul className="divide-y rounded border text-sm">
              {test.attempts.map((a) => (
                <li key={a.id} className="flex items-center gap-2 px-3 py-2">
                  {a.status === 'passed' ? (
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                  ) : a.status === 'failed' ? (
                    <XCircle className="h-4 w-4 text-destructive" />
                  ) : (
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="flex-1">
                    Attempt #{a.attemptNumber} —{' '}
                    {a.status === 'pending_review' ? 'awaiting review' : `${a.scorePct ?? 0}%`}
                  </span>
                  {a.status !== 'pending_review' && (
                    <button
                      className="text-xs text-primary hover:underline"
                      onClick={() => setViewAttemptId(a.id)}
                    >
                      Review
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AttemptResult({
  attempt,
  test,
  onRetake,
  onBack,
}: {
  attempt: MemberAttempt;
  test: CourseTestState;
  onRetake: (() => void) | null;
  onBack: () => void;
}) {
  const pending = attempt.status === 'pending_review';
  return (
    <Card>
      <CardContent className="space-y-4 py-5">
        <div className="space-y-1 text-center">
          {attempt.status === 'passed' ? (
            <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
          ) : pending ? (
            <Clock className="mx-auto h-10 w-10 text-muted-foreground" />
          ) : (
            <XCircle className="mx-auto h-10 w-10 text-destructive" />
          )}
          <h2 className="text-lg font-semibold">
            {test.isPractice
              ? `Practice complete — ${attempt.scorePct ?? 0}%`
              : attempt.status === 'passed'
                ? 'Passed!'
                : pending
                  ? 'Awaiting review'
                  : 'Not this time'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {pending
              ? 'Your typed answers are with your instructor — check back soon.'
              : `Score: ${attempt.scorePct ?? 0}% · pass mark ${test.passingScorePct}%`}
          </p>
        </div>
        {!pending && (
          <ul className="space-y-2">
            {attempt.entries.map((e, i) => (
              <li key={e.questionId} className="rounded border p-3 text-sm">
                <p className="font-medium">
                  {i + 1}. {e.prompt}
                </p>
                {e.kind === 'multiple_choice' ? (
                  <>
                    <p className="mt-1 flex items-center gap-1 text-muted-foreground">
                      {e.correct ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 text-destructive" />
                      )}
                      Your answer:{' '}
                      {e.selectedIndex != null ? (e.choices[e.selectedIndex] ?? '—') : '—'}
                    </p>
                    {/* Practice attempts only: a graded payload omits both keys,
                        which is what keeps the answer key server-side there. */}
                    {e.solutionIndex != null && !e.correct && (
                      <p className="mt-0.5 text-muted-foreground">
                        Correct answer: {e.choices[e.solutionIndex] ?? '—'}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="mt-1 text-muted-foreground">
                    {e.awardedPoints}/{e.points} pts
                    {e.comment && <span className="block italic">“{e.comment}”</span>}
                  </p>
                )}
                {e.explanation && (
                  <p className="mt-1 text-xs italic text-muted-foreground">{e.explanation}</p>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack}>
            Back
          </Button>
          {(attempt.status === 'failed' || test.isPractice) && onRetake && (
            <Button size="sm" onClick={onRetake}>
              {test.isPractice ? 'Retake practice' : 'Retake test'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
